<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import SlideOver from '@/components/shared/SlideOver.vue'
import { useOrganisationCredits } from '@/composables/useOrganisationCredits'
import type { OrganisationCredits } from '@/types/credits'

const props = defineProps<{
  open: boolean
  organisationId: number
  currentBalance: number
}>()

const emit = defineEmits<{
  close: []
  adjusted: [credits: OrganisationCredits]
}>()

const { t } = useI18n()
const { credits, actionLoading, actionError, fieldErrors, adjustCredits } = useOrganisationCredits()

// An empty number input yields '' with v-model.number.
const amount = ref<number | ''>('')
const localError = ref<string | null>(null)

const parsedAmount = computed<number | null>(() =>
  typeof amount.value === 'number' && Number.isInteger(amount.value) ? amount.value : null,
)

const newBalance = computed<number | null>(() =>
  parsedAmount.value === null ? null : props.currentBalance + parsedAmount.value,
)

const amountError = computed<string | null>(() => fieldErrors.value.amount?.[0] ?? localError.value)

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    amount.value = ''
    localError.value = null
    fieldErrors.value = {}
    actionError.value = null
  },
)

function handleClose(): void {
  if (actionLoading.value) return
  emit('close')
}

function validate(): boolean {
  localError.value = null
  fieldErrors.value = {}
  if (parsedAmount.value === null || parsedAmount.value === 0) {
    localError.value = t('admin.organisations.credits_error_amount')
    return false
  }
  if (props.currentBalance + parsedAmount.value < 0) {
    localError.value = t('admin.organisations.credits_error_negative', { balance: props.currentBalance.toLocaleString() })
    return false
  }
  return true
}

async function handleSubmit(): Promise<void> {
  if (!validate() || parsedAmount.value === null) return

  const success = await adjustCredits(props.organisationId, parsedAmount.value)

  if (success && credits.value) {
    emit('adjusted', credits.value)
    emit('close')
  }
}
</script>

<template>
  <SlideOver
    :open="open"
    :title="t('admin.organisations.credits_adjust')"
    :saving="actionLoading"
    @close="handleClose"
  >
    <div
      v-if="actionError"
      class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300"
      role="alert"
    >
      {{ t(actionError) }}
    </div>

    <div>
      <label for="adjust-credits-amount" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('admin.organisations.credits_amount') }}
      </label>
      <input
        id="adjust-credits-amount"
        v-model.number="amount"
        type="number"
        step="1"
        class="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
        :class="{ 'border-red-500': amountError }"
      />
      <p v-if="amountError" class="mt-1 text-xs text-red-400">{{ amountError }}</p>
      <p v-else class="mt-1 text-xs text-slate-500">{{ t('admin.organisations.credits_amount_help') }}</p>
      <p
        v-if="newBalance !== null"
        class="mt-2 text-sm"
        :class="newBalance < 0 ? 'text-red-400' : 'text-slate-300'"
      >
        {{ t('admin.organisations.credits_new_balance', { count: newBalance.toLocaleString() }) }}
      </p>
    </div>

    <template #footer>
      <button
        type="button"
        :disabled="actionLoading"
        class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
        @click="handleClose"
      >
        {{ t('admin.common.cancel') }}
      </button>
      <button
        type="button"
        :disabled="actionLoading"
        class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        @click="handleSubmit"
      >
        {{ t('admin.organisations.credits_save') }}
      </button>
    </template>
  </SlideOver>
</template>
